import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PageHelp from "@/components/PageHelp";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Instagram, Linkedin, Music2, Pin, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SocialAccountsPage() {
  const [companies, setCompanies] = useState([]);
  const [connections, setConnections] = useState([]);
  const [providerStatus, setProviderStatus] = useState({});
  const { t } = useLanguage();

  const load = async () => {
    try {
      const [c, cs, ps] = await Promise.all([
        axios.get(`${API}/companies`, { withCredentials: true }),
        axios.get(`${API}/social/connections`, { withCredentials: true }),
        axios.get(`${API}/social/providers/status`, { withCredentials: true }),
      ]);
      setCompanies(c.data);
      setConnections(cs.data?.items || []);
      setProviderStatus(ps.data || {});
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  // Rueckkehr aus dem OAuth-Flow
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected")) {
      toast.success(`${params.get("connected")} erfolgreich verbunden`);
      load();
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("connect_error")) {
      toast.error(`OAuth-Fehler: ${params.get("connect_error")}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line

  const startConnect = async (platform, companyId) => {
    if (!companyId) { toast.info("Keine Firma vorhanden"); return; }
    try {
      const r = await axios.get(`${API}/social/connect/${platform}/start`, {
        params: { company_id: companyId },
        withCredentials: true,
      });
      window.location.href = r.data.redirect_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect fehlgeschlagen");
    }
  };

  const disconnect = async (connId) => {
    if (!window.confirm("Verbindung trennen? Posts auf diesem Kanal sind danach nicht mehr moeglich.")) return;
    try {
      await axios.delete(`${API}/social/connections/${connId}`, { withCredentials: true });
      toast.success("Getrennt");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  return (
    <div data-testid="social-accounts-page" className="space-y-6 max-w-5xl">
      <PageHelp title="Wie verknuepfe ich Instagram, Facebook, TikTok oder LinkedIn?" storageKey="social-accounts">
        <h4>Meta (Instagram + Facebook)</h4>
        <ol>
          <li><b>Einmal</b> auf „Meta verbinden" klicken und im Facebook-Dialog <b>alle Seiten + Instagram-Konten anhaken</b>.</li>
          <li>Danach erscheint jede Seite in der Liste — pro Seite die Firma waehlen und auf „Zuweisen" klicken. Fertig.</li>
        </ol>
        <h4>LinkedIn / TikTok</h4>
        <p>Pro Firma auf „Verbinden" klicken und beim jeweiligen Netzwerk einloggen.</p>
        <p>
          ⚠ <b>Voraussetzung:</b> Die Developer-App-Credentials muessen einmalig unter{" "}
          <a href="/admin/oauth-credentials"><b>Admin → OAuth-Apps</b></a> hinterlegt sein.
        </p>
      </PageHelp>

      <div>
        <h1 className="text-4xl font-bold font-heading tracking-tight">{t("social.title")}</h1>
        <p className="text-[var(--studio-text-muted)] mt-1">Verbinde Instagram, Facebook, LinkedIn, TikTok und Pinterest mit deinen Firmen</p>
      </div>

      <MetaCard
        companies={companies}
        connections={connections}
        status={providerStatus.meta || {}}
        onConnect={startConnect}
        onDisconnect={disconnect}
        onChanged={load}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PerCompanyCard
          platform="linkedin"
          label="LinkedIn"
          icon={Linkedin}
          color="#0A66C2"
          companies={companies}
          connections={connections}
          status={providerStatus.linkedin || {}}
          onConnect={startConnect}
          onDisconnect={disconnect}
        />
        <PerCompanyCard
          platform="tiktok"
          label="TikTok"
          icon={Music2}
          color="#000000"
          companies={companies}
          connections={connections}
          status={providerStatus.tiktok || {}}
          onConnect={startConnect}
          onDisconnect={disconnect}
        />
        <PerCompanyCard
          platform="pinterest"
          label="Pinterest"
          icon={Pin}
          color="#E60023"
          companies={companies}
          connections={connections}
          status={providerStatus.pinterest || {}}
          onConnect={startConnect}
          onDisconnect={disconnect}
          onChanged={load}
        />
      </div>
    </div>
  );
}

function BoardSelect({ conn, onChanged }) {
  const [saving, setSaving] = useState(false);
  const boards = conn.boards || [];
  const select = async (boardId) => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/social/connections/${conn.id}/select-board`, { board_id: boardId }, { withCredentials: true });
      toast.success(r.data.message);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };
  if (!boards.length) {
    return <p className="text-[11px] text-amber-600 mt-1">Keine Boards gefunden — bitte zuerst auf Pinterest ein Board erstellen, dann neu verbinden</p>;
  }
  return (
    <select
      className="mt-1 h-7 w-full text-xs border border-[var(--studio-border)] rounded bg-[var(--studio-surface)] px-1"
      value={conn.selected_board_id || ""}
      disabled={saving}
      onChange={(e) => e.target.value && select(e.target.value)}
      data-testid={`pinterest-board-select-${conn.company_id}`}
    >
      <option value="">Board wählen…</option>
      {boards.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
    </select>
  );
}

function MissingCreds({ platform }) {
  return (
    <div className="flex flex-col gap-1 text-xs text-amber-700 bg-amber-50 p-3 rounded border border-amber-200">
      <div className="flex items-start gap-1.5">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>App-Credentials fehlen.</span>
      </div>
      <a
        href="/admin/oauth-credentials"
        className="text-amber-900 underline font-semibold"
        data-testid={`goto-oauth-creds-${platform}`}
      >
        → Hier eintragen
      </a>
    </div>
  );
}

function MetaCard({ companies, connections, status, onConnect, onDisconnect, onChanged }) {
  const [assigning, setAssigning] = useState(null);
  const [assignCompany, setAssignCompany] = useState({});

  const metaConns = connections.filter((c) => c.platform === "meta");
  // Neueste Verbindung = aktueller Facebook-Grant (alle teilen denselben Token)
  const conn = metaConns[0];
  const pages = conn?.pages || [];

  // page_id -> company_id (ueber alle Meta-Verbindungen)
  const selectedByPage = {};
  metaConns.forEach((c) => { if (c.selected_page_id) selectedByPage[c.selected_page_id] = c.company_id; });
  const companyName = (id) => companies.find((c) => c.id === id)?.name || "?";

  const guessCompany = (pageName) => {
    const generic = new Set(["gmbh", "studio", "gruppe", "group"]);
    const pn = (pageName || "").toLowerCase();
    const hit = companies.find((c) =>
      c.name.toLowerCase().split(/[\s-]+/).some((w) => w.length > 3 && !generic.has(w) && pn.includes(w))
    );
    return hit?.id || "";
  };

  const assignPage = async (pageId, companyId) => {
    if (!companyId) { toast.info("Bitte Firma fuer diese Seite waehlen"); return; }
    setAssigning(pageId);
    try {
      const r = await axios.post(
        `${API}/social/connections/${conn.id}/select-page`,
        { page_id: pageId, company_id: companyId },
        { withCredentials: true }
      );
      toast.success(r.data?.message || "Seite zugewiesen");
      if (r.data && !r.data.instagram_linked) {
        toast.info("Hinweis: Diese Seite hat kein verknuepftes Instagram-Konto — nur Facebook-Publishing aktiv.");
      }
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zuweisung fehlgeschlagen");
    } finally {
      setAssigning(null);
    }
  };

  const disconnectAll = () => {
    if (!metaConns.length) return;
    if (!window.confirm("Meta komplett trennen? Instagram/Facebook-Posts sind danach nicht mehr moeglich.")) return;
    Promise.all(metaConns.map((c) => axios.delete(`${API}/social/connections/${c.id}`, { withCredentials: true })))
      .then(() => { toast.success("Meta getrennt"); onChanged?.(); })
      .catch(() => toast.error("Fehler beim Trennen"));
  };

  return (
    <Card className="border-[var(--studio-border)] shadow-none" data-testid="meta-card">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#E4405F22", color: "#E4405F" }}>
              <Instagram className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium">Meta (Instagram + Facebook)</p>
              {conn ? (
                <p className="text-xs text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Verbunden
                </p>
              ) : (
                <p className="text-xs text-[var(--studio-text-muted)]">Nicht verbunden</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {conn && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={() => onConnect("meta", conn.company_id)}
                  data-testid="meta-reconnect-btn"
                >
                  <RefreshCw className="w-3 h-3 mr-1" /> Neu verbinden
                </Button>
                <button onClick={disconnectAll} className="text-red-500 text-xs hover:underline" data-testid="meta-disconnect-btn">
                  Trennen
                </button>
              </>
            )}
          </div>
        </div>

        {!status.configured ? (
          <MissingCreds platform="meta" />
        ) : !conn ? (
          <div className="space-y-2">
            <Button
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-9 text-sm"
              onClick={() => onConnect("meta", companies[0]?.id)}
              data-testid="connect-meta"
            >
              Meta verbinden
            </Button>
            <p className="text-xs text-[var(--studio-text-muted)]">
              Wichtig: Im Facebook-Dialog <b>alle Seiten und Instagram-Konten anhaken</b> — die Zuordnung zu den Firmen machst du danach hier.
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="meta-pages-list">
            <p className="text-xs font-semibold text-[var(--studio-text-muted)]">Seiten → Firmen zuordnen</p>
            {pages.length === 0 && (
              <p className="text-xs text-amber-700">
                Keine Seiten freigegeben — bitte „Neu verbinden" und im Facebook-Dialog die Seiten anhaken.
              </p>
            )}
            {pages.map((pg) => {
              const assignedTo = selectedByPage[pg.page_id];
              const target = assignCompany[pg.page_id] ?? guessCompany(pg.name);
              return (
                <div
                  key={pg.page_id}
                  className={`flex items-center justify-between gap-3 p-2.5 rounded-md border text-sm flex-wrap ${
                    assignedTo ? "bg-blue-50 border-blue-200" : "border-[var(--studio-border)]"
                  }`}
                  data-testid={`meta-page-row-${pg.page_id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{pg.name}</p>
                    <p className={`text-xs ${pg.instagram_username ? "text-pink-600" : "text-amber-600"}`}>
                      {pg.instagram_username ? `IG: @${pg.instagram_username}` : "Kein Instagram verknuepft"}
                    </p>
                  </div>
                  {assignedTo ? (
                    <span className="flex items-center gap-1.5 text-blue-700 text-xs font-medium shrink-0" data-testid={`page-assigned-${pg.page_id}`}>
                      <CheckCircle2 className="w-4 h-4" /> {companyName(assignedTo)}
                    </span>
                  ) : (
                    <div className="flex items-center gap-2 shrink-0">
                      <select
                        value={target}
                        onChange={(e) => setAssignCompany((m) => ({ ...m, [pg.page_id]: e.target.value }))}
                        className="h-8 text-xs rounded-md border border-[var(--studio-border)] bg-white px-2"
                        data-testid={`assign-company-${pg.page_id}`}
                      >
                        <option value="">Firma waehlen...</option>
                        {companies.map((co) => (
                          <option key={co.id} value={co.id}>{co.name}</option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        className="h-8 text-xs bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                        onClick={() => assignPage(pg.page_id, target)}
                        disabled={assigning === pg.page_id}
                        data-testid={`assign-page-${pg.page_id}`}
                      >
                        {assigning === pg.page_id ? "..." : "Zuweisen"}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PerCompanyCard({ platform, label, icon: Icon, color, companies, connections, status, onConnect, onDisconnect, onChanged }) {
  const connFor = (companyId) =>
    connections.find((c) => c.platform === platform && c.company_id === companyId && c.status === "active");

  return (
    <Card className="border-[var(--studio-border)] shadow-none" data-testid={`${platform}-card`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "22", color }}>
            <Icon className="w-5 h-5" />
          </div>
          <p className="font-medium">{label}</p>
        </div>

        {!status.configured ? (
          <MissingCreds platform={platform} />
        ) : (
          <div className="space-y-1.5">
            {companies.map((co) => {
              const conn = connFor(co.id);
              return (
                <div
                  key={co.id}
                  className="flex items-center justify-between gap-2 p-2 rounded-md border border-[var(--studio-border)] text-sm"
                  data-testid={`${platform}-company-row-${co.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{co.name}</p>
                    {conn && (
                      <p className="text-xs text-emerald-700 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> {conn.account_name || "Verbunden"}
                      </p>
                    )}
                    {conn && platform === "pinterest" && (
                      <BoardSelect conn={conn} onChanged={onChanged} />
                    )}
                  </div>
                  {conn ? (
                    <button
                      onClick={() => onDisconnect(conn.id)}
                      className="text-red-500 text-xs hover:underline shrink-0"
                      data-testid={`${platform}-disconnect-${co.id}`}
                    >
                      Trennen
                    </button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs shrink-0"
                      onClick={() => onConnect(platform, co.id)}
                      data-testid={`${platform}-connect-${co.id}`}
                    >
                      Verbinden
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
